

import React, { memo, useRef, useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import _isEmpty from 'lodash.isempty';

import _debounce from 'lodash.debounce';
import { useEditModeManager } from '../../../../../../../common/hooks';
import editModeManager from '../../../../../../../common/utils/EditModeManager';
import { isLightColor } from '../../../../../../SplashRenderer';
import { Icon } from '../../../../../../../ui-kit';

import { OptionsBlock } from '../Actions';
import {
  Visible,
  Edit,
  Mail,
  AnchorLink,
  TextColor,
  FillColor,
  BorderColor } from
'../Actions/components/OptionsList';
import { ActionTypes } from '../../../../types';
import { RelativeSpanBlock } from './components/common/StyledEditables';

// eslint-disable-next-line no-template-curly-in-string
const mailToLink = 'mailto:${email}';

const ButtonBase = styled.button.attrs((props) => ({
  style: {
    ...props.style,
    borderWidth: '1px',
    ...(props.inEditingMode ? {
      boxShadow: 'rgb(203 239 232) 0px 0px 2px 8px',
      borderWidth: '3px',
      borderColor: 'rgb(34, 189, 163)'
    } : {}),
    ...(!props.isVisible ? {
      opacity: 0.25
    } : {})
  }
}))`${(props) => props.computedStyle}`;

const ButtonWrapper = styled.div.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;

const ButtonContent = styled.div.attrs((props) => ({
  style: { ...props.style }
}))`${(props) => props.computedStyle}`;

const colorPickerPositions = {
  position: 'absolute',
  top: '40px',
  zIndex: '9'
};










const ButtonGroup = (props) => {
  const { buttons, container, handleTemplateChange, moduleId, rowId, columnId } = props;
  const { style: containerStyle, computedStyle: containerComputedStyle } = container;

  const [editableButtonId, setEditableButtonId] = useState('');
  const [isTextColorPickerVisible, setTextColorPickerVisible] = useState(false);
  const [isBackgroundColorPickerVisible, setBackgroundColorPickerVisible] = useState(false);
  const [isBorderColorPickerVisible, setBorderColorPickerVisible] = useState(false);
  const [isButtonTextEditVisible, setButtonTextEditVisible] = useState(false);
  const [isButtonLinkEditVisible, setButtonLinkEditVisible] = useState(false);
  const [isButtonMailEditVisible, setButtonMailEditVisible] = useState(false);
  const [inputData, setInputData] = useState(null);

  const componentId = useMemo(() => `buttongroup-${moduleId || 'unknown'}-${rowId || 'unknown'}-${columnId || 'unknown'}`, [moduleId, rowId, columnId]);

  const componentRef = useRef([]);

  const setRef = (uuid, node) => {
    componentRef.current[uuid] = node;
  };

  const hideTextColorPicker = () => {
    setTextColorPickerVisible(false);
  };

  const hideBackgroundColorPicker = () => {
    setBackgroundColorPickerVisible(false);
  };

  const hideBorderColorPicker = () => {
    setBorderColorPickerVisible(false);
  };

  const hideButtonTextEdit = () => {
    setButtonTextEditVisible(false);
  };

  const hideButtonLinkEdit = () => {
    setButtonLinkEditVisible(false);
  };

  const hideButtonMailEdit = () => {
    setButtonMailEditVisible(false);
  };

  const hideOptions = () => {
    hideBackgroundColorPicker();
    hideTextColorPicker();
    hideBorderColorPicker();
    hideButtonTextEdit();
    hideButtonLinkEdit();
    hideButtonMailEdit();
  };

  useEffect(() => {
    if (editableButtonId) {
      hideOptions();
    }

    function handleClick(e) {
      if (editableButtonId && componentRef.current[editableButtonId]) {
        const ref = componentRef.current[editableButtonId];
        if (!ref.contains(e.target)) {
          editModeManager.clearActiveComponent();
          setEditableButtonId('');
          hideOptions();
        }
      }
    }

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [editableButtonId]);

  useEditModeManager({
    componentId,
    callbackToHide: () => {
      setEditableButtonId('');
      hideOptions();
    },
    optionalConditionToHide: editableButtonId || isTextColorPickerVisible ||
    isBackgroundColorPickerVisible || isBorderColorPickerVisible ||
    isButtonTextEditVisible || isButtonLinkEditVisible || isButtonMailEditVisible,
    dependencies: [
    editableButtonId,
    isTextColorPickerVisible,
    isBackgroundColorPickerVisible,
    isBorderColorPickerVisible,
    isButtonTextEditVisible,
    isButtonLinkEditVisible,
    isButtonMailEditVisible]

  });

  const showTextColorPicker = () => {
    setTextColorPickerVisible(true);
    hideBackgroundColorPicker();
    hideBorderColorPicker();
    hideButtonTextEdit();
    hideButtonLinkEdit();
    hideButtonMailEdit();
  };

  const showBackgroundColorPicker = () => {
    setBackgroundColorPickerVisible(true);
    hideTextColorPicker();
    hideBorderColorPicker();
    hideButtonTextEdit();
    hideButtonLinkEdit();
    hideButtonMailEdit();
  };

  const showBorderColorPicker = () => {
    hideBackgroundColorPicker();
    hideTextColorPicker();
    setBorderColorPickerVisible(true);
    hideButtonTextEdit();
    hideButtonLinkEdit();
    hideButtonMailEdit();
  };

  const showButtonTextEdit = () => {
    hideBackgroundColorPicker();
    hideTextColorPicker();
    hideBorderColorPicker();
    hideButtonLinkEdit();
    setButtonTextEditVisible(true);
    hideButtonMailEdit();

    const buttonDetails = buttons.find((button) => button.uuid === editableButtonId) || {};

    setInputData({
      placeholder: 'Enter button text',
      isRequired: true,
      isLink: false,
      value: buttonDetails.buttonText || '',
      style: { ...colorPickerPositions, left: '40px', top: '60px' }
    });
  };

  const showButtonLinkEdit = () => {
    hideBackgroundColorPicker();
    hideTextColorPicker();
    hideBorderColorPicker();
    hideButtonTextEdit();
    setButtonLinkEditVisible(true);
    hideButtonMailEdit();

    const buttonDetails = buttons.find((button) => button.uuid === editableButtonId) || {};
    const buttonLink = buttonDetails.buttonLink || '';
    setInputData({
      placeholder: 'Enter hyperlink',
      isRequired: false,
      isLink: true,
      isEmail: true,
      value: buttonLink !== mailToLink ? buttonLink : '',
      style: { ...colorPickerPositions, left: '70px', top: '60px' }
    });
  };

  const showButtonMailEdit = () => {
    hideBackgroundColorPicker();
    hideTextColorPicker();
    hideBorderColorPicker();
    hideButtonTextEdit();
    hideButtonLinkEdit();
    setButtonMailEditVisible(true);

    setInputData({
      placeholder: 'Enter hyperlink',
      isRequired: false,
      isLink: true,
      isEmail: true,
      value: mailToLink,
      isDisabled: true,
      style: { ...colorPickerPositions, left: '70px', top: '60px' }
    });
  };

  const setIsVisible = () => {
    const buttonGroups = buttons.map((button) => {
      if (button.uuid === editableButtonId) {
        return {
          ...button,
          isVisible: !button.isVisible
        };
      }

      return button;
    });

    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          buttons: buttonGroups
        }
      }
    });
    hideOptions();
  };

  const handlePickerColorChange = ({ hex = '' } = {}) => {
    const buttonGroups = buttons.map((button) => {
      if (button.uuid !== editableButtonId) {
        return button;
      }

      return {
        ...button,
        ...(isTextColorPickerVisible ? {
          buttonTextStyles: {
            ...button.buttonTextStyles,
            color: hex.toUpperCase()
          }
        } : {}),
        style: {
          ...button.style,
          ...(isBackgroundColorPickerVisible ? {
            background: hex.toUpperCase()
          } : {}),
          ...(isBorderColorPickerVisible ? {
            borderColor: hex.toUpperCase()
          } : {})
        }
      };
    });

    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          buttons: buttonGroups
        }
      }
    });
    if (hex === 'TRANSPARENT') {
      if (isBackgroundColorPickerVisible) {
        hideBackgroundColorPicker();
      }
      if (isBorderColorPickerVisible) {
        hideBorderColorPicker();
      }
    }
  };

  const handleInputChanges = (value) => {
    const buttonGroups = buttons.map((button) => {
      if (button.uuid !== editableButtonId) {
        return button;
      }

      return {
        ...button,
        ...(isButtonTextEditVisible ? {
          buttonText: value
        } : {}),
        ...(isButtonLinkEditVisible ? {
          buttonLink: value
        } : {}),
        ...(isButtonMailEditVisible ? {
          buttonLink: mailToLink
        } : {})
      };
    });

    handleTemplateChange(ActionTypes.UPDATE_BLOCK, {
      data: {
        moduleDescriptorValueToSet: {
          buttons: buttonGroups
        }
      }
    });

    if (isButtonTextEditVisible) {
      hideButtonTextEdit();
    } else if (isButtonMailEditVisible) {
      hideButtonMailEdit();
    } else {
      hideButtonLinkEdit();
    }
  };

  const colorPickerOptions = (buttonOptions) => [
  {
    visible: isTextColorPickerVisible,
    initialValue: buttonOptions.buttonTextStyles.color || '#1A1F24',
    style: {
      ...colorPickerPositions,
      left: '80px'
    }
  },
  {
    visible: isBackgroundColorPickerVisible,
    initialValue: buttonOptions.style.background || 'transparent',
    style: {
      ...colorPickerPositions,
      left: '178px'
    }
  },
  {
    visible: isBorderColorPickerVisible,
    initialValue: buttonOptions.style.borderColor || 'transparent',
    style: {
      ...colorPickerPositions,
      left: '265px'
    }
  }];


  const options = (buttonOptions) => {
    const { buttonLink = '' } = buttonOptions;
    return [
    {
      action: setIsVisible,
      label:
      <Visible isVisible={buttonOptions.isVisible} />

    },
    {
      action: isButtonTextEditVisible ? hideButtonTextEdit : showButtonTextEdit,
      isActive: isButtonTextEditVisible,
      label:
      <Edit />

    },
    {
      action: isButtonLinkEditVisible ? hideButtonLinkEdit : showButtonLinkEdit,
      isActive: isButtonLinkEditVisible ||
      !_isEmpty(buttonLink) && buttonLink !== mailToLink,

      label:
      <AnchorLink />

    },
    {
      action: isButtonMailEditVisible ? hideButtonMailEdit : showButtonMailEdit,
      isActive: isButtonMailEditVisible ||
      !_isEmpty(buttonLink) && buttonLink === mailToLink,

      label:
      <Mail />

    },
    {
      action: isTextColorPickerVisible ? hideTextColorPicker : showTextColorPicker,
      isActive: isTextColorPickerVisible,
      label:
      <TextColor active={isTextColorPickerVisible} style={buttonOptions.buttonTextStyles} />

    },
    {
      action: isBackgroundColorPickerVisible ?
      hideBackgroundColorPicker :
      showBackgroundColorPicker,
      isActive: isBackgroundColorPickerVisible,
      label:
      <FillColor active={isBackgroundColorPickerVisible} style={buttonOptions.style} />

    },
    {
      action: isBorderColorPickerVisible ? hideBorderColorPicker : showBorderColorPicker,
      isActive: isBorderColorPickerVisible,
      label:
      <BorderColor active={isBorderColorPickerVisible} style={buttonOptions.style} />

    }];

  };

  const handleBlockClick = (e, buttonDetails) => {
    e.stopPropagation();

    if (buttonDetails.isEditable) {
      editModeManager.setActiveComponent(componentId);
      setEditableButtonId(buttonDetails.uuid);
    }

    return void 0;
  };

  return (
    <ButtonWrapper style={containerStyle} computedStyle={containerComputedStyle}>
      {buttons.map((button) => {
        const { buttonTextStyles } = button;
        const { color: buttonTextColor } = buttonTextStyles || {};

        return (
          <RelativeSpanBlock key={button.uuid} ref={(node) => setRef(button.uuid, node)}>
            <ButtonBase
              key={button.uuid}
              className="button"
              onClick={(e) => handleBlockClick(e, button)}
              style={button.style}
              inEditingMode={editableButtonId === button.uuid}
              computedStyle={button.computedStyle}
              isVisible={button.isVisible}>

              <ButtonContent
                className="button__content"
                style={{
                  ...button.buttonTextStyles,
                  ...(button.buttonLink && button.buttonLink !== '' ? {
                    paddingRight: '15px'
                  } : {})
                }}>

                {button.buttonText}
              </ButtonContent>
              {
              button.buttonLink && button.buttonLink !== '' &&
              <Icon
                name="link"
                color={isLightColor(buttonTextColor) ? 'white' : 'black'}
                size={16} />}


            </ButtonBase>
            {editableButtonId === button.uuid &&
            <OptionsBlock
              style={button}
              handlePickerColorChange={_debounce(handlePickerColorChange, 10)}
              options={options(button)}
              colorPickerOptions={colorPickerOptions(button)}
              isTextEditVisible={isButtonTextEditVisible}
              isLinkEditVisible={isButtonLinkEditVisible}
              isMailEditVisible={isButtonMailEditVisible}
              inputData={inputData}
              handleInputChanges={handleInputChanges}
              showTransparentButton={!isTextColorPickerVisible} />}


          </RelativeSpanBlock>);

      }
      )}
    </ButtonWrapper>);

};

export default memo(ButtonGroup);